/**
 * Quote Repository (Refactored)
 *
 * This is a facade that provides backward compatibility while delegating
 * to specialized modules:
 * - QuoteQueryRepository: Read operations
 * - QuoteCommandRepository: Write operations
 * - QuoteStatisticsService: Statistics & analytics
 * - QuoteWorkflowService: Business logic & workflows
 *
 * This refactoring improves maintainability by separating concerns and
 * reducing file size from 735 lines to ~180 lines per module.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { Quote } from '@/shared/types';

// Import specialized modules
import { quoteQueryRepository } from './QuoteQueryRepository';
import { quoteCommandRepository } from './QuoteCommandRepository';
import { quoteStatisticsService } from './QuoteStatisticsService';
import type { QuoteStats } from './QuoteStatisticsService';
import { quoteWorkflowService } from './QuoteWorkflowService';

/**
 * Quote Repository (Facade)
 *
 * Provides a unified interface to quote operations while delegating
 * to specialized modules. This maintains backward compatibility with
 * existing code while improving code organization.
 *
 * @deprecated Prefer using the specialized modules directly:
 * - Use quoteQueryRepository for read operations
 * - Use quoteCommandRepository for write operations
 * - Use quoteStatisticsService for statistics
 * - Use quoteWorkflowService for business logic
 */
export class QuoteRepository extends BaseRepository<Quote> {
  protected tableName = 'quotes' as const;

  // ============================================================================
  // QUERY OPERATIONS (delegated to QuoteQueryRepository)
  // ============================================================================

  /**
   * Find all quotes for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    return quoteQueryRepository.findAll(userId, options);
  }

  /**
   * Find a single quote by ID with relations
   */
  async findById(id: string): Promise<RepositoryResult<Quote>> {
    return quoteQueryRepository.findById(id);
  }

  /**
   * Find quotes by user ID (returns array, not paginated)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Quote[]>> {
    return quoteQueryRepository.findByUserId(userId);
  }

  /**
   * Get quotes by client
   */
  async findByClient(
    userId: string,
    clientId: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    return quoteQueryRepository.findByClient(userId, clientId, options);
  }

  /**
   * Get quotes by status
   */
  async findByStatus(
    userId: string,
    status: Quote['status'],
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    return quoteQueryRepository.findByStatus(userId, status, options);
  }

  /**
   * Find quote by public token (for client portal)
   */
  async findByPublicToken(token: string): Promise<RepositoryResult<Quote>> {
    return quoteQueryRepository.findByPublicToken(token);
  }

  /**
   * Check if a quote has dependencies
   */
  async hasDependencies(id: string): Promise<RepositoryResult<boolean>> {
    return quoteQueryRepository.hasDependencies(id);
  }

  // ============================================================================
  // COMMAND OPERATIONS (delegated to QuoteCommandRepository)
  // ============================================================================

  /**
   * Create a new quote with items
   */
  async create(
    userId: string,
    quoteData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<RepositoryResult<Quote>> {
    // Generate quote number and public token via workflow service
    const quoteNumber = await quoteWorkflowService.generateQuoteNumber(userId);
    const publicToken = quoteWorkflowService.generatePublicToken();
    return quoteCommandRepository.create(userId, quoteData, quoteNumber, publicToken);
  }

  /**
   * Update an existing quote
   */
  async update(id: string, quoteData: Partial<Quote>): Promise<RepositoryResult<Quote>> {
    return quoteCommandRepository.update(id, quoteData);
  }

  /**
   * Delete a quote
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    return quoteCommandRepository.delete(id);
  }

  /**
   * Update quote status
   */
  async updateStatus(id: string, status: Quote['status']): Promise<RepositoryResult<Quote>> {
    return quoteCommandRepository.updateStatus(id, status);
  }

  // ============================================================================
  // STATISTICS OPERATIONS (delegated to QuoteStatisticsService)
  // ============================================================================

  /**
   * Get quote statistics for a user
   */
  async getStats(userId: string): Promise<RepositoryResult<QuoteStats>> {
    return quoteStatisticsService.getStats(userId);
  }

  // ============================================================================
  // RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
  // ============================================================================

  /**
   * Quote Query Repository
   * Use this for read-only operations on quotes
   */
  static Query = quoteQueryRepository;

  /**
   * Quote Command Repository
   * Use this for write operations on quotes
   */
  static Command = quoteCommandRepository;

  /**
   * Quote Statistics Service
   * Use this for statistical and analytical operations
   */
  static Statistics = quoteStatisticsService;

  /**
   * Quote Workflow Service
   * Use this for business logic and workflow operations
   */
  static Workflow = quoteWorkflowService;
}

// Export singleton instance for backward compatibility
export const quoteRepository = new QuoteRepository();

// ============================================================================
// RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
// ============================================================================

/**
 * Quote Query Repository
 * Use this for read-only operations on quotes
 */
export { quoteQueryRepository };

/**
 * Quote Command Repository
 * Use this for write operations on quotes
 */
export { quoteCommandRepository };

/**
 * Quote Statistics Service
 * Use this for statistical and analytical operations
 */
export {
  quoteStatisticsService,
  QuoteStats,
  // Also export additional types from QuoteStatisticsService
  type QuotePerformanceMetrics,
};

// Import types from QuoteStatisticsService for re-export
import type { QuotePerformanceMetrics } from './QuoteStatisticsService';

/**
 * Quote Workflow Service
 * Use this for business logic and workflow operations
 */
export { quoteWorkflowService };
